// src/utils/generateCatalogPDF.js
//
// Бумажный каталог-журнал: страницы А4, карточки товаров сеткой.
// Карточка = фото + название + штрихкод (полосками) + цифры.
//
// Содержит ТРИ вещи вместе:
//   1) кириллица (шрифт Roboto — jsPDF из коробки её не умеет);
//   2) честная раскладка (карточки фикс. высоты, ряд переносится целиком —
//      ничего не режется; категория может занять несколько листов — это норм);
//   3) устойчивая загрузка фото (без crossOrigin, фолбэк, таймаут).
//
// Режимы: 'simple' — все подряд; 'byCategory' — категория с новой страницы.

import { jsPDF } from 'jspdf';
import { drawEan13 } from './ean13Pdf';

// ── Кириллический шрифт ────────────────────────────────────────
let cachedFont = null;
async function loadCyrillicFont() {
  if (cachedFont) return cachedFont;
  try {
    const ttfUrl = 'https://cdn.jsdelivr.net/npm/roboto-font@0.1.0/fonts/Roboto/roboto-regular-webfont.ttf';
    const res = await fetch(ttfUrl);
    if (!res.ok) throw new Error('font fetch failed');
    const buf = await res.arrayBuffer();
    let binary = '';
    const bytes = new Uint8Array(buf);
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    cachedFont = btoa(binary);
    return cachedFont;
  } catch (e) {
    console.warn('Шрифт кириллицы не загружен:', e);
    return null;
  }
}

// ── Загрузка фото в dataURL ────────────────────────────────────
// Сначала без crossOrigin (так вставлялось раньше). Если холст «грязный»
// (кросс-домен) и toDataURL кидает — повтор с crossOrigin (сервер отдаёт CORS).
// Таймаут — чтобы одна медленная картинка не вешала всю генерацию.
function loadImageDataUrl(url) {
  return new Promise((resolve) => {
    if (!url) return resolve(null);
    let done = false;
    const fin = (v) => { if (!done) { done = true; clearTimeout(timer); resolve(v); } };
    const timer = setTimeout(() => fin(null), 8000);

    const attempt = (useCors) => {
      const img = new Image();
      if (useCors) img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const size = 300;
          canvas.width = size; canvas.height = size;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, size, size);
          const r = Math.min(size / img.width, size / img.height);
          const w = img.width * r, h = img.height * r;
          ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
          fin(canvas.toDataURL('image/jpeg', 0.8));
        } catch (e) {
          if (!useCors) attempt(true); else fin(null);
        }
      };
      img.onerror = () => { if (!useCors) attempt(true); else fin(null); };
      img.src = url;
    };

    attempt(false);
  });
}

/**
 * groups: [{ title, items:[{name,barcode,photo}] }]
 * opts: { mode: 'simple'|'byCategory' }
 * onProgress(done,total)
 */
export async function generateCatalogPDF(groups, opts = {}, onProgress) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  // шрифт
  const fontB64 = await loadCyrillicFont();
  if (fontB64) {
    doc.addFileToVFS('Roboto-Regular.ttf', fontB64);
    doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
    doc.setFont('Roboto');
  }
  const FONT = fontB64 ? 'Roboto' : 'helvetica';

  // геометрия
  const pageW = 210, pageH = 297;
  const margin = 10;
  const gap = 4;
  const cols = 3;

  const cellW = (pageW - margin * 2 - gap * (cols - 1)) / cols;
  const pad = 2;
  const imgW = cellW - pad * 2;
  const imgH = 38;
  const nameH = 7;
  const barcodeH = 9;
  const digitsH = 4;
  const cellH = pad + imgH + 2 + nameH + 1 + barcodeH + digitsH + pad;

  let y = margin;
  let firstPage = true;
  const newPage = () => { doc.addPage(); y = margin; };

  const drawHeader = (title) => {
    doc.setFont(FONT, 'normal');
    doc.setFontSize(14);
    doc.setTextColor(30, 30, 30);
    doc.text(String(title || ''), margin, y + 5);
    y += 10;
  };

  const drawCard = (it, x, cardY, photoData) => {
    doc.setDrawColor(210);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, cardY, cellW, cellH, 1.5, 1.5, 'S');

    if (photoData) {
      try { doc.addImage(photoData, 'JPEG', x + pad, cardY + pad, imgW, imgH); } catch (e) {}
    } else {
      doc.setFillColor(244, 244, 244);
      doc.rect(x + pad, cardY + pad, imgW, imgH, 'F');
    }

    // «Ноунэйм» — вместо фото крупный знак «?», чтобы карточку было видно сразу
    if (it.noNameMark) {
      doc.setFont(FONT, 'normal');
      doc.setFontSize(64);
      doc.setTextColor(150, 150, 150);
      doc.text(String(it.noNameMark), x + cellW / 2, cardY + pad + imgH / 2 + 8, { align: 'center' });
      doc.setTextColor(20, 20, 20);
    }


    // Номер на весах (PLU) — крупной плашкой в углу фото, как шпаргалка у весов
    if (it.plu != null && it.plu !== '') {
      const badgeW = 16, badgeH = 8;
      const bx = x + pad + imgW - badgeW - 1.5;
      const by = cardY + pad + 1.5;
      doc.setFillColor(217, 131, 36);           // охра
      doc.roundedRect(bx, by, badgeW, badgeH, 1.2, 1.2, 'F');
      doc.setFont(FONT, 'normal');
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text('№' + String(it.plu), bx + badgeW / 2, by + badgeH - 2.4, { align: 'center' });
      doc.setTextColor(20, 20, 20);
    }

    let cy = cardY + pad + imgH + 3.5;
    doc.setFont(FONT, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(20, 20, 20);
    const lines = doc.splitTextToSize(String(it.name || ''), cellW - pad * 2).slice(0, 2);
    doc.text(lines, x + cellW / 2, cy, { align: 'center' });
    cy += nameH + 1;

    drawEan13(doc, it.barcode, x + pad, cy, cellW - pad * 2, barcodeH, true);
  };

  // предзагрузка фото
  const all = groups.flatMap(g => g.items);
  let loaded = 0;
  const photoCache = new Map();
  for (const it of all) {
    if (it.photo && !photoCache.has(it.photo)) {
      photoCache.set(it.photo, await loadImageDataUrl(it.photo));
    }
    loaded++;
    if (onProgress) onProgress(loaded, all.length);
  }

  // отрисовка
  for (const g of groups) {
    if (opts.mode === 'byCategory') {
      if (!firstPage) newPage();
      firstPage = false;
      drawHeader(g.title);
    }

    let col = 0;
    for (const it of g.items) {
      if (col === 0 && y + cellH > pageH - margin) {
        newPage();
        if (opts.mode === 'byCategory') drawHeader(g.title + ' …');
      }
      const x = margin + col * (cellW + gap);
      drawCard(it, x, y, photoCache.get(it.photo));
      col++;
      if (col >= cols) { col = 0; y += cellH + gap; }
      firstPage = false;
    }
    if (col > 0) { y += cellH + gap; }
  }

  return doc.output('blob');
}
